import { Link } from "wouter";
import { ArrowLeft } from "lucide-react";

export default function TermsConditions() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-chart-2/5 to-primary/10">
      <div className="container max-w-4xl mx-auto px-4 py-12">
        <Link href="/" className="inline-flex items-center gap-2 text-primary hover:underline mb-8" data-testid="link-home">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <h1 className="text-4xl md:text-5xl font-bold text-primary mb-8" data-testid="text-terms-title">
          Terms of Use
        </h1>

        <div className="bg-background/80 backdrop-blur-sm rounded-lg p-6 md:p-8 space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4" data-testid="text-section-acceptance">
              1. Acceptance of Terms
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                EduFocal provides a collection of interactive online resources, including content and practice questions in a variety of academic subject areas, and messaging services (referred to hereafter as "the Service"), for students subject to the following Terms of Use ("TOU"). By using or accessing the Service in any way, you (or your parent or guardian on your behalf, if you are under the age of 18) agree to comply with the TOU, and abide by any applicable posted guidelines for the Service, which may change from time to time.
              </p>
              <p>
                Should you object to any term or condition of the TOU, any guidelines, or any subsequent modifications thereto or become dissatisfied with EduFocal in any way, your only recourse is to immediately discontinue use of EduFocal. EduFocal has the right, but not the obligation, to enforce the TOU through means including, but not limited to, investigation, termination of your account, and/or litigation.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4" data-testid="text-section-modifications">
              2. Modifications to this Agreement
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                We reserve the right, at our sole discretion, to change, modify or otherwise alter these terms and conditions at any time. Such modifications shall become effective immediately upon the posting thereof. You must review this agreement on a regular basis to keep yourself apprised of any changes.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4" data-testid="text-section-content">
              3. Content
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                You understand that all student generated content (including text or images) posted on, transmitted through, or linked from the Service, is the sole responsibility of the person from whom such content originated. Consequently, you are solely responsible for all content that you post, email or otherwise make available via the Service.
              </p>
              <p>
                You understand that, while EduFocal endeavors to pre-screen student generated content, it does not do so in every case, and is thus not responsible for student generated content made available through the Service, and that by using the Service, you may be exposed to content that is offensive, indecent, inaccurate, misleading, or otherwise objectionable.
              </p>
              <p>
                Furthermore, the EduFocal site and content available through the Service may contain links to other websites, which are completely independent of EduFocal. EduFocal makes no representation or warranty as to the accuracy, completeness or authenticity of the information contained in any such site.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4" data-testid="text-section-infringement">
              4. Notification of Claims of Infringement
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                If you believe that content on the EduFocal site constitutes copyright infringement of work belonging to you, or that your intellectual property rights have been otherwise violated, please notify EduFocal at:
              </p>
              <p className="font-medium"><a href="mailto:hello@edufocalgroup.com" className="hover:underline">hello@edufocalgroup.com</a></p>
              <p>or:</p>
              <div className="pl-4">
                <p>EduFocal Limited,</p>
                <p>16 Chelsea Avenue,</p>
                <p>Kingston 10,</p>
                <p>Jamaica W.I.</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4" data-testid="text-section-privacy">
              5. Privacy and Information Disclosure
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                EduFocal has established a Privacy Policy to explain to users how their information is collected and used. Your use of the EduFocal website or the Service signifies acknowledgement of and agreement to our Privacy Policy.
              </p>
              <p>
                You further acknowledge and agree that EduFocal may, in its sole discretion, preserve or disclose your content, as well as your information, such as email addresses, IP addresses, timestamps, and other user information, if required to do so by law or in the good faith belief that such preservation or disclosure is reasonably necessary to: comply with legal process; enforce the TOU; respond to claims that any content violates the rights of third-parties; or protect the rights, property, or personal safety of EduFocal, its users or the general public.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4" data-testid="text-section-conduct">
              6. Conduct
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p className="font-medium">You agree not to post, email, or otherwise make available content that:</p>
              <ul className="space-y-2 pl-4">
                <li className="flex gap-3">
                  <span className="text-primary">01.</span>
                  <span>is unlawful, harmful, threatening, abusive, harassing, defamatory, libelous, invasive of another's privacy, or is harmful to minors in any way;</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary">02.</span>
                  <span>is pornographic or depicts nudity or any sexual conduct;</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary">03.</span>
                  <span>harasses, degrades, intimidates or is hateful toward an individual or group of individuals on the basis of religion, gender, sexual orientation, race, ethnicity, age, or disability;</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary">04.</span>
                  <span>violates any law or regulation;</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary">05.</span>
                  <span>impersonates any person or entity, or falsely states or otherwise misrepresents your affiliation with a person or entity;</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary">06.</span>
                  <span>includes personal or identifying information about another person without that person's explicit consent;</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary">07.</span>
                  <span>is false, deceptive, misleading, deceitful, or misinformative;</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary">08.</span>
                  <span>constitutes or contains spam, chain letters, pyramid schemes, or unsolicited commercial advertisement;</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary">09.</span>
                  <span>advertises any illegal service or the sale of any items the sale of which is prohibited or restricted by any applicable law;</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary">10.</span>
                  <span>contains software viruses or any other computer code designed to interrupt, destroy or limit the functionality of any computer software or hardware;</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary">11.</span>
                  <span>disrupts the normal flow of dialogue or otherwise negatively affects other users' ability to use the Service;</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary">12.</span>
                  <span>employs misleading email addresses, or forged headers or otherwise manipulated identifiers in order to disguise the origin of content transmitted through the Service.</span>
                </li>
              </ul>
              <p className="font-medium mt-6">Additionally, you agree not to:</p>
              <ul className="space-y-2 pl-4">
                <li className="flex gap-3">
                  <span className="text-primary">14.</span>
                  <span>contact anyone who has asked not to be contacted, or make unsolicited contact with anyone for any commercial purpose;</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary">15.</span>
                  <span>"stalk" or otherwise harass anyone;</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary">16.</span>
                  <span>collect personal data about other users for commercial or unlawful purposes;</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary">17.</span>
                  <span>use automated means, including spiders, robots, crawlers, data mining tools, or the like to download data from the Service;</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary">18.</span>
                  <span>post content that is not relevant to the Service or impose an unreasonable or disproportionately large load on our infrastructure;</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary">19.</span>
                  <span>attempt to gain unauthorized access to EduFocal's computer systems or engage in any activity that disrupts or interferes with the performance of the Service;</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary">20.</span>
                  <span>use any form of automated posting device;</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary">21.</span>
                  <span>share your login or password with others, or use the login or password information of someone else.</span>
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4" data-testid="text-section-spam">
              7. No Spam Policy
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                You understand and agree that sending unsolicited email advertisements to EduFocal users or through EduFocal computer systems is expressly prohibited by the TOU, and any unauthorized use of EduFocal computer systems is a violation of the TOU, as well as certain laws, including without limitation the Cybercrimes Act. Such violations may subject you or your agents to civil and criminal penalties.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4" data-testid="text-section-limitations">
              8. Limitations on Service
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                The Service is directed to members of academic communities, including students and faculty, and use of the Service, or certain aspects thereof, may be limited to such individuals.
              </p>
              <p>
                You acknowledge that EduFocal may establish limits concerning use of the Service, including the maximum number of days that content will be retained by the Service, the maximum number and size of postings, and the frequency with which you may access the Service. EduFocal reserves the right at any time to modify or discontinue the Service (or any part thereof) with or without notice.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4" data-testid="text-section-access">
              9. Access to the Service
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                For good and valuable consideration received, including without limitation, payment of the annual fee, EduFocal grants you a limited, revocable, nonexclusive license to access the Service for your own personal use for one (1) year. This license does not include any collection, aggregation, copying, duplication, display or derivative use of the Service.
              </p>
              <p>
                Registered users are prohibited from sharing their login or password information with others, including specifically, but not limited to, persons who are not members of academic communities.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4" data-testid="text-section-termination">
              10. Termination of Service
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                You agree that EduFocal, in its sole discretion, has the right (but not the obligation) to delete or deactivate your account, block your email or IP address, or otherwise terminate your access to or use of the Service (or any part thereof), immediately and without notice, for any reason, including, without limitation, if EduFocal believes that you have violated the letter or spirit of the TOU.
              </p>
              <p>
                Further, you agree that EduFocal shall not be liable to you or any third-party for any termination of your access to the Service. No refunds will be given for early termination in accordance with this Section.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4" data-testid="text-section-proprietary">
              11. Proprietary Rights
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                EduFocal reserves all rights in the Service, content displayed on or through the Service, and any intellectual property associated with or embodied by the Service. Any reproduction, modification, creation of derivative works from or redistribution of the site or the collective work is prohibited without the express written consent of EduFocal.
              </p>
              <p>
                You further agree not to reproduce, duplicate or copy content from the Service without the express written consent of EduFocal, and agree to abide by any and all copyright notices displayed on the Service. You may not decompile or disassemble, reverse engineer or otherwise attempt to discover any source code contained in the Service.
              </p>
            </div>
          </section>

          <div className="mt-8 pt-6 border-t border-border">
            <p className="text-sm text-muted-foreground" data-testid="text-last-updated">
              Last updated: October 1, 2025
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
